import React from 'react';
import { useTranslation } from 'react-i18next';
import FlowCard from './FlowCard';
import ActionButtons from './ActionButtons';

/**
 * The button-plus-context step cards. Each surfaces a title/body + the relevant
 * actions; when the current user has no available action (e.g. it's the other
 * party's turn), only the context text shows.
 */
const STEP_META = {
  RequestCard: {
    step: 'flow.step_request',
    title: 'flow.request_title',
    body: 'flow.request_body',
    only: ['request', 'decline'],
  },
  ArrivalCard: {
    step: 'flow.step_arrival',
    title: 'flow.arrival_title',
    body: 'flow.arrival_body',
    only: ['mark_here', 'cancel', 'mark_done'],
  },
  WaitingCard: {
    step: 'flow.step_waiting',
    title: 'flow.waiting_title',
    body: 'flow.waiting_body',
    only: ['mark_here', 'report_no_show', 'cancel', 'mark_done'],
  },
  ConfirmPresenceCard: {
    step: 'flow.step_arrival',
    title: 'flow.confirm_presence_title',
    body: 'flow.confirm_presence_body',
    only: ['confirm_presence', 'dispute_presence', 'cancel'],
  },
  HandoffCard: {
    step: 'flow.step_handoff',
    title: 'flow.handoff_title',
    body: 'flow.handoff_body',
    only: ['confirm_handoff'],
  },
};

function makeStepCard(name) {
  const meta = STEP_META[name];
  const Card = ({ actions = [], dispatch, busy, roleColorVar }) => {
    const { t } = useTranslation('common');
    return (
      <FlowCard step={t(meta.step)} title={t(meta.title)} roleColorVar={roleColorVar}>
        <p>{t(meta.body)}</p>
        <div className="flow-card-actions">
          <ActionButtons actions={actions} dispatch={dispatch} busy={busy} only={meta.only} />
        </div>
      </FlowCard>
    );
  };
  Card.displayName = name;
  return Card;
}

export const RequestCard = makeStepCard('RequestCard');
export const ArrivalCard = makeStepCard('ArrivalCard');
export const ConfirmPresenceCard = makeStepCard('ConfirmPresenceCard');

// WaitingCard is custom: copy reflects whether you're the one waiting or the one
// still to arrive, and if you're the absent party bailing while the other waits
// past the meeting time, cancel becomes an attributed no-show (swap the button + warn).
export function WaitingCard({ actions = [], dispatch, busy, roleColorVar }) {
  const { t } = useTranslation('common');
  const canMarkHere = actions.some((a) => a.action === 'mark_here' && a.guardOk);
  const lateCancel = actions.some((a) => a.action === 'cancel_as_no_show' && a.guardOk);
  const only = ['mark_here', 'report_no_show', 'mark_done', lateCancel ? 'cancel_as_no_show' : 'cancel'];
  const bodyKey = lateCancel
    ? 'flow.waiting_late_body'
    : canMarkHere
      ? 'flow.waiting_arrive_body'
      : 'flow.waiting_body';
  return (
    <FlowCard step={t('flow.step_waiting')} title={t('flow.waiting_title')} roleColorVar={roleColorVar}>
      <p>{t(bodyKey)}</p>
      <div className="flow-card-actions">
        <ActionButtons actions={actions} dispatch={dispatch} busy={busy} only={only} />
      </div>
    </FlowCard>
  );
}
WaitingCard.displayName = 'WaitingCard';

// HandoffCard is custom: prompt to confirm vs. "waiting for them to confirm".
export function HandoffCard({ actions = [], dispatch, busy, roleColorVar }) {
  const { t } = useTranslation('common');
  const canConfirm = actions.some((a) => a.action === 'confirm_handoff' && a.guardOk);
  return (
    <FlowCard step={t('flow.step_handoff')} title={t('flow.handoff_title')} roleColorVar={roleColorVar}>
      <p>{t(canConfirm ? 'flow.handoff_body' : 'flow.handoff_waiting_body')}</p>
      <div className="flow-card-actions">
        <ActionButtons actions={actions} dispatch={dispatch} busy={busy} only={['confirm_handoff']} />
      </div>
    </FlowCard>
  );
}
HandoffCard.displayName = 'HandoffCard';
